/**
 * Routes where the side navigation does not render (#15618) — kept beside
 * `src/lib/ai-chat/hidden-paths.ts` in spirit: one pure predicate, one list.
 *
 * - `/auth/*`, `/no-app-access`, `/trial-expired` — outside the app; there
 *   is nothing to navigate to. `/auth` is segment-anchored (`isAuthRoute`),
 *   so `/authoring` keeps its sidebar. `/unauthorized` is NOT here since
 *   #16727: a signed-in viewer refused a page keeps the app's only
 *   navigation, which the deleted top bar used to give them.
 * - `/meetings/<id>/room*` and `/meetings/<id>/presenter*` — the facilitation
 *   surfaces own a full-screen frame and mount the header `sr-only`. The
 *   meetings LIST and a meeting's detail page are ordinary pages and keep it.
 * - `/admin/**` — draws its OWN sai-ui sidebar (#14982,
 *   docs/design-system/ADOPTION_DECISIONS.md § 5, Option B). Both are
 *   `position: fixed` on the left edge, so drawing this one too would stack
 *   two rails. That own sidebar is `AdminSideNav` (#15688), which carries
 *   Back to app, Search, Notifications and Send feedback, and whose inset
 *   puts the header in its bar-less mode; with no resolvable viewer the
 *   admin layout falls back to `AdminSidebar` and the header keeps its
 *   minimal bar.
 *
 * On the other routes here the header keeps its minimal bar
 * (`useAppChromeSidebarRendered` reads `false`): the brand link, and no
 * signed-in navigation (#16727).
 * Signed-out and error pages are handled elsewhere: the root layout mounts no
 * shell without a viewer, and an error page's header hides the sidebar
 * through its `data-side-nav-suppressed` marker.
 *
 * Everything else keeps the sidebar — including the pulse conversation
 * (#15784), an ordinary in-app page whose shortcut suppression (#3671) lives
 * in the header and does not depend on the sidebar being hidden.
 */

import { isAuthRoute } from '@/lib/routes/auth-routes'

const HIDDEN_EXACT_OR_NESTED = [
  '/no-app-access',
  '/trial-expired',
  '/admin',
] as const

const FACILITATION_SURFACE = /^\/meetings\/[^/]+\/(room|presenter)(\/|$)/

export function shouldHideSidebar(pathname: string): boolean {
  if (isAuthRoute(pathname)) return true
  if (FACILITATION_SURFACE.test(pathname)) return true
  return HIDDEN_EXACT_OR_NESTED.some(
    path => pathname === path || pathname.startsWith(`${path}/`),
  )
}
